import { useEffect, useRef, useState } from "react";
import { Alert, AlertTitle, LinearProgress, Portal, Snackbar, Typography } from "@mui/material";

type ToastSeverity = "success" | "error" | "warning" | "info";

interface ToastNotificationProps {
  open: boolean;
  message: string;
  title?: string;
  severity: ToastSeverity;
  onClose: () => void;
  duration?: number;
}

export default function ToastNotification({
  open,
  message,
  title,
  severity,
  onClose,
  duration = 5000,
}: ToastNotificationProps) {
  const [progress, setProgress] = useState(100);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setProgress(100);
      return;
    }

    const startedAt = Date.now();
    let closeTimer: number | undefined;
    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));

      if (elapsed >= duration) {
        window.clearInterval(timer);
        closeTimer = window.setTimeout(() => onCloseRef.current(), 100);
      }
    }, 50);

    return () => {
      window.clearInterval(timer);
      if (closeTimer !== undefined) window.clearTimeout(closeTimer);
    };
  }, [duration, open, message, title]);

  return (
    <Portal><Snackbar
      open={open}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      onClose={(_, reason) => {
        if (reason !== "clickaway") onClose();
      }}
      sx={{ mt: 7, width: { xs: "calc(100% - 32px)", sm: 380 },
        maxWidth: { xs: "calc(100% - 32px)", sm: 380 } }}
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={onClose}
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          overflowY: "auto",
          maxHeight: "calc(100vh - 96px)",
          "@supports (height: 100dvh)": { maxHeight: "calc(100dvh - 96px)" },
          p: 2,
          alignItems: "flex-start",
          "& .MuiAlert-message": { minWidth: 0 },
        }}
      >
        {title && <AlertTitle sx={{ mb: 0.5, fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}>{title}</AlertTitle>}
        <Typography component="div" variant="body2" sx={{ fontWeight: 400, lineHeight: 1.55, overflowWrap: "anywhere" }}>{message}</Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            left: 0,
            height: 3,
            bgcolor: "rgba(255,255,255,0.3)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "common.white",
              transition: "none",
            },
          }}
        />
      </Alert>
    </Snackbar></Portal>
  );
}
